import type { FC } from 'react'
import type { Faq } from '@/constants/home'

import { ArrowUpRight } from 'lucide-react'
import { Accordion } from '@/components/kit/accordion'
import { PageLink } from '@/components/kit/page-link'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'

interface FaqSectionProps {
    items: Array<Faq>
    index?: string
    lines?: Array<string>
    id?: string
}

export const FaqSection: FC<FaqSectionProps> = ({
    items,
    index,
    lines = ['Quick questions,', 'straight answers'],
    id = 'faq'
}) => {
    return (
        <section
            id={id}
            aria-labelledby={`${id}-title`}
            className='mx-auto grid max-w-[1400px] gap-12 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:gap-10'
        >
            <div className='lg:col-span-4'>
                <div className='lg:sticky lg:top-28'>
                    <SectionLabel index={index}>Questions</SectionLabel>
                    <SplitText
                        as='h2'
                        id={`${id}-title`}
                        lines={lines}
                        className='mt-5 text-[clamp(2.5rem,5vw,4.5rem)] font-bold leading-[0.92] tracking-[-0.04em]'
                    />

                    <Reveal delay={200}>
                        <PageLink
                            href='/contact#book-a-call'
                            className='group mt-10 flex items-center justify-between gap-4 rounded-[1.5rem] border border-black/5 bg-paper p-5 transition-colors hover:bg-ink hover:text-cream'
                        >
                            <span>
                                <span className='block font-semibold'>Something we did not cover?</span>
                                <span className='mt-1 block text-sm text-stone group-hover:text-cream/70'>Book a free 20 minute call. No slides, just answers.</span>
                            </span>
                            <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-ember text-ink transition-transform duration-500 ease-expo group-hover:rotate-45'>
                                <ArrowUpRight className='size-5' />
                            </span>
                        </PageLink>
                    </Reveal>
                </div>
            </div>

            <Reveal
                delay={150}
                className='lg:col-span-8'
            >
                <Accordion items={items} />
            </Reveal>
        </section>
    )
}
